import { httpClient } from '@/utils/httpClient';
import { formatNomenclatureWithSize } from '@equip/utils';
import { outputReport, getOutputFormat } from '@/utils/reportOutput';
import { getPersonLabels } from '@/hooks/usePersonLabels';
import {
  buildAssetsApproachingExpirationReportHtml,
  type AssetsApproachingExpirationItem,
  type AssetsApproachingExpirationTemplateOptions,
} from '@/reports/templates/assetsApproachingExpirationTemplate';
import { entityService } from '@/services/EntityService';
import { splitLocationBreadcrumb } from '@/utils/locationHierarchy';

interface AssetEntityRow {
  id: string;
  display_name: string;
  entity_data: {
    lin?: string;
    nsn?: string;
    size?: string;
    expiration_date?: string;
    location_id?: string;
  };
}

interface LocationHierarchyRow {
  id: string;
  breadcrumb: string | null;
}

interface HandReceiptEntityRow {
  id: string;
  entity_data: {
    asset_id?: string;
    person_id?: string;
  };
}

const chunk = <T>(values: T[], size: number): T[][] => {
  if (size <= 0) return [values];
  const chunks: T[][] = [];
  for (let i = 0; i < values.length; i += size) {
    chunks.push(values.slice(i, i + size));
  }
  return chunks;
};

const normalizeDateOnly = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const date = new Date(trimmed);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString().slice(0, 10);
};

const normalizeScope = (value: unknown): 'issued' | 'inventory' | 'both' => {
  if (typeof value !== 'string') return 'both';
  const normalized = value.trim().toLowerCase();
  if (normalized.startsWith('iss')) return 'issued';
  if (normalized.startsWith('inv')) return 'inventory';
  if (normalized === 'both' || normalized === 'all') return 'both';
  return 'both';
};

const PAGE_SIZE = 1000;

const listAllEntitiesByQuery = async <T extends { id?: string }>(baseQuery: URLSearchParams): Promise<T[]> => {
  const allRows: T[] = [];
  let lastId: string | undefined;

  // Ensure order includes id as a tiebreaker for keyset pagination
  const existingOrder = baseQuery.get('order');
  if (existingOrder && !/\bid\b/.test(existingOrder)) {
    baseQuery.set('order', `${existingOrder},id.asc`);
  } else if (!existingOrder) {
    baseQuery.set('order', 'id.asc');
  }

  while (true) {
    const pageQuery = new URLSearchParams(baseQuery.toString());
    pageQuery.set('limit', String(PAGE_SIZE));
    if (lastId) {
      pageQuery.set('id', `gt.${lastId}`);
    }

    const page = await httpClient.get<T[]>(`/api/v1/entities?${pageQuery.toString()}`);
    allRows.push(...page);

    if (page.length < PAGE_SIZE) {
      break;
    }

    const last = page[page.length - 1];
    if (last?.id) {
      lastId = last.id;
    } else {
      break;
    }
  }

  return allRows;
};

export const runAssetsApproachingExpirationReport = async (
  params: Record<string, unknown>,
): Promise<void> => {
  const expiringByDate = normalizeDateOnly(params.expiringByDate);
  const scope = normalizeScope(params.assetScope);

  const today = new Date();
  const todayStart = new Date(
    Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()),
  );

  const endDate = expiringByDate
    ? new Date(expiringByDate + 'T00:00:00Z')
    : new Date(todayStart.getTime() + 60 * 24 * 3600 * 1000);
  const endMs = endDate.getTime() + 24 * 3600 * 1000 - 1;
  const startMs = todayStart.getTime();

  const todayIso = todayStart.toISOString().slice(0, 10);
  const endIso = endDate.toISOString().slice(0, 10);

  const assetQuery = new URLSearchParams({
    entity_type: 'eq.asset',
    is_current: 'eq.true',
    order: 'id.asc',
    select: 'id,display_name,entity_data',
    'entity_data->>expiration_date': `gte.${todayIso}`,
  });
  // PostgREST does not support multiple filters on the same column via URLSearchParams,
  // so append the upper-bound filter manually.
  assetQuery.append('entity_data->>expiration_date', `lte.${endIso}`);
  const rows = await listAllEntitiesByQuery<AssetEntityRow>(assetQuery);

  // Fetch active hand receipts to get asset → person mapping (assets don't have person_id directly)
  const personIdByAssetId = new Map<string, string>();
  const handReceiptQuery = new URLSearchParams({
    select: 'id,entity_data',
    entity_type: 'eq.hand_receipt',
    status: 'eq.active',
    is_current: 'eq.true',
    order: 'id.asc',
  });

  const handReceipts = await listAllEntitiesByQuery<HandReceiptEntityRow>(handReceiptQuery);

  for (const hr of handReceipts) {
    const assetId = hr.entity_data?.asset_id;
    const personId = hr.entity_data?.person_id;
    if (assetId && personId) {
      personIdByAssetId.set(assetId, personId);
    }
  }

  const personIds = Array.from(new Set(personIdByAssetId.values()));

  const personNameById = new Map<string, string>();
  for (const batchIds of chunk(personIds, 75)) {
    const idFilter = batchIds.length === 1 ? `eq.${batchIds[0]}` : `in.(${batchIds.join(',')})`;
    const people = await entityService.list({
      type: 'person',
      limit: batchIds.length,
      order: 'id.asc',
      filters: {
        id: idFilter,
        select: 'id,display_name',
      },
    });
    for (const person of people) {
      personNameById.set(person.id, person.display_name);
    }
  }

  const locationIds = Array.from(
    new Set(
      rows
        .map((row) => row.entity_data.location_id)
        .filter((id): id is string => typeof id === 'string' && id.trim().length > 0),
    ),
  );

  const breadcrumbByLocationId = new Map<string, string>();
  for (const batchIds of chunk(locationIds, 75)) {
    const idFilter = batchIds.length === 1 ? `eq.${batchIds[0]}` : `in.(${batchIds.join(',')})`;
    const locationQuery = new URLSearchParams({
      select: 'id,breadcrumb',
      id: idFilter,
      limit: String(batchIds.length),
    }).toString();

    const locations = await httpClient.get<LocationHierarchyRow[]>(
      `/api/v1/location_hierarchy?${locationQuery}`,
    );

    for (const location of locations) {
      if (location.breadcrumb) {
        breadcrumbByLocationId.set(location.id, location.breadcrumb);
      }
    }
  }

  const grouped = new Map<string, AssetsApproachingExpirationItem>();

  for (const row of rows) {
    const expiration = row.entity_data.expiration_date ?? null;
    if (!expiration) continue;

    const expDate = new Date(expiration);
    if (Number.isNaN(expDate.getTime())) continue;

    const expMs = expDate.getTime();
    if (expMs < startMs || expMs > endMs) {
      continue;
    }

    const personId = personIdByAssetId.get(row.id) ?? null;
    const isIssued = Boolean(personId);
    if (scope === 'issued' && !isIssued) continue;
    if (scope === 'inventory' && isIssued) continue;

    const locationId = row.entity_data.location_id?.trim() || null;

    const key = [
      row.entity_data.lin ?? '',
      row.entity_data.nsn ?? '',
      row.display_name ?? '',
      expiration,
      personId ?? '',
      locationId ?? '',
    ].join('|');

    const existing = grouped.get(key);
    if (existing) {
      existing.quantity = (existing.quantity ?? 0) + 1;
      continue;
    }

    const individual = personId ? (personNameById.get(personId) ?? null) : null;
    const breadcrumb = locationId ? (breadcrumbByLocationId.get(locationId) ?? null) : null;
    const segments = breadcrumb ? splitLocationBreadcrumb(breadcrumb) : [];

    const site = segments[0] ?? null;
    const location = segments.length > 1 ? segments[1] : null;
    const sublocation = segments.length > 2 ? segments.slice(2).join(' / ') : null;

    grouped.set(key, {
      lin: row.entity_data.lin ?? null,
      nsn: row.entity_data.nsn ?? null,
      nomenclature: formatNomenclatureWithSize(row.display_name, row.entity_data.size as string | undefined),
      expirationDate: expiration,
      quantity: 1,
      individual,
      site,
      location,
      sublocation,
    });
  }

  const items = Array.from(grouped.values()).sort((a, b) => {
    const dateA = a.expirationDate ? new Date(a.expirationDate).getTime() : 0;
    const dateB = b.expirationDate ? new Date(b.expirationDate).getTime() : 0;
    if (dateA !== dateB) return dateA - dateB;
    return (a.nomenclature ?? '').localeCompare(b.nomenclature ?? '');
  });

  const options: AssetsApproachingExpirationTemplateOptions = {
    personLabels: getPersonLabels(params),
    generatedAt: new Date(),
    header: {
      expiringByDate: expiringByDate ?? endDate.toISOString().slice(0, 10),
      assetScope: scope,
    },
    items,
  };

  const html = buildAssetsApproachingExpirationReportHtml(options);
  outputReport(html, 'assets-approaching-expiration-report.html', getOutputFormat(params));
};
